import { Tonality } from "@mui/icons-material";
import { Divider, Skeleton, Stack } from "@mui/material";
import {
  ActionItemAzureDevOpsWorkItemsProperty,
  ActionItemDueDateProperty,
  ActionItemMetricProperty,
  ActionItemNoteProperty,
  ActionItemOwnershipProperty,
  ActionItemStatusPicker,
  ActionItemTeamProperty,
  SupportingIssuesProperty,
  SupportingStrategicItemsProperty,
  SupportingTaskItemsProperty,
} from ".";
import { ItemProperty } from "..";
import { ActionItemPropertyType, DEFAULT_ACTION_ITEM_STATUS, SupportingItemType } from "../../constants";
import { ActionItemDetailDto, UserDto } from "../../http";
import { ITeamSummary } from "../../models";
import { ITeamSection } from "../EditActionItemSectionModal";

interface IProps {
  actionItem: ActionItemDetailDto | undefined;
  propertyVisibility?: {
    [key in ActionItemPropertyType]?: boolean;
  };
  isLoading?: boolean;
  onTeamChanged?: (selectedTeam: ITeamSummary | null, selectedSection: ITeamSection | null) => void;
  onOwnershipChanged?: (actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) => void;
  onSupportingItemClicked?: (itemType: SupportingItemType, itemGuid: string) => void;
}

const ActionItemProperties = (props: IProps) => {
  return (
    <>
      {props.isLoading && (
        <Stack
          spacing={0.5}
          sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5 }}
          divider={<Divider flexItem sx={{ borderStyle: "dashed" }} />}
        >
          <Skeleton width={"80%"} />
          <Skeleton width={"80%"} />
          <Skeleton width={"80%"} />
        </Stack>
      )}

      {!props.isLoading && props.actionItem && (
        <Stack
          spacing={0.5}
          sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5 }}
          divider={<Divider flexItem sx={{ borderStyle: "dashed" }} />}
        >
          <ActionItemTeamProperty actionItem={props.actionItem} onTeamChanged={props.onTeamChanged} />

          {(!props.propertyVisibility || props.propertyVisibility?.status) && (
            <ItemProperty labelIcon={<Tonality />} label="Status" rowLayoutOnSmallScreens>
              <ActionItemStatusPicker
                variant="select"
                actionItemGuid={props.actionItem.guid}
                status={props.actionItem.currentStatus ?? DEFAULT_ACTION_ITEM_STATUS}
                planGuid={props.actionItem.teamPlanSection.planGuid}
                disabled={!props.actionItem.userCanEdit}
              />
            </ItemProperty>
          )}

          {(!props.propertyVisibility || props.propertyVisibility?.ownership) && (
            <ActionItemOwnershipProperty actionItem={props.actionItem} onChanged={props.onOwnershipChanged} />
          )}

          {(!props.propertyVisibility || props.propertyVisibility?.notes) && (
            <ActionItemNoteProperty actionItem={props.actionItem} />
          )}

          {(!props.propertyVisibility || props.propertyVisibility?.metrics) && (
            <ActionItemMetricProperty actionItem={props.actionItem} />
          )}

          {(!props.propertyVisibility || props.propertyVisibility.dueDate) && (
            <ActionItemDueDateProperty actionItem={props.actionItem} />
          )}

          {(!props.propertyVisibility || props.propertyVisibility?.supportingItems) && (
            <SupportingStrategicItemsProperty
              items={props.actionItem.supportingItems.actionItems}
              context={{
                teamName: props.actionItem.teamPlanSection.teamName,
                teamSlug: props.actionItem.teamPlanSection.teamSlug,
                actionItemGuid: props.actionItem.guid,
                actionItemDescription: props.actionItem.description,
              }}
              teamPlanSection={props.actionItem.teamPlanSection}
              onClick={props.onSupportingItemClicked}
            />
          )}

          {(!props.propertyVisibility || props.propertyVisibility?.supportingItems) && (
            <SupportingTaskItemsProperty
              items={props.actionItem.supportingItems.taskItems}
              context={{
                teamName: props.actionItem.teamPlanSection.teamName,
                teamSlug: props.actionItem.teamPlanSection.teamSlug,
                actionItemGuid: props.actionItem.guid,
                actionItemDescription: props.actionItem.description,
              }}
              onClick={props.onSupportingItemClicked}
            />
          )}

          {(!props.propertyVisibility || props.propertyVisibility?.supportingItems) && (
            <SupportingIssuesProperty
              items={props.actionItem.supportingItems.issues}
              context={{
                teamName: props.actionItem.teamPlanSection.teamName,
                teamSlug: props.actionItem.teamPlanSection.teamSlug,
                actionItemGuid: props.actionItem.guid,
                actionItemDescription: props.actionItem.description,
              }}
              onClick={props.onSupportingItemClicked}
            />
          )}

          {(!props.propertyVisibility || props.propertyVisibility?.azureDevOps) && (
            <ActionItemAzureDevOpsWorkItemsProperty actionItem={props.actionItem} />
          )}
        </Stack>
      )}
    </>
  );
};

export { ActionItemProperties };
